import { createSlice, PayloadAction } from '@reduxjs/toolkit';

export interface NotificationData {
  id: string;
  type: 'success' | 'error' | 'info' | 'warning';
  title: string;
  message: string;
  duration?: number; // Auto-close duration in ms, 0 for no auto-close
  actionText?: string;
  actionUrl?: string;
  onAction?: () => void;
  userId?: string; // To ensure notifications are user-specific
  createdAt: number;
}

interface NotificationState {
  notifications: NotificationData[];
}

const initialState: NotificationState = {
  notifications: []
};

const notificationSlice = createSlice({
  name: 'notifications',
  initialState,
  reducers: {
    addNotification: (state, action: PayloadAction<Omit<NotificationData, 'id' | 'createdAt'>>) => {
      const notification: NotificationData = {
        ...action.payload,
        id: `notification_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
        createdAt: Date.now()
      };
      state.notifications.push(notification);
    },
    
    removeNotification: (state, action: PayloadAction<string>) => {
      state.notifications = state.notifications.filter(
        notification => notification.id !== action.payload
      );
    },
    
    clearNotifications: (state) => {
      state.notifications = [];
    },
    
    clearUserNotifications: (state, action: PayloadAction<string>) => {
      state.notifications = state.notifications.filter(
        notification => notification.userId !== action.payload
      );
    },
    
    // Specific notification creators for summary generation flow
    addUploadSuccessNotification: (state, action: PayloadAction<{ userId: string; filename?: string; processId?: string }>) => {
      const processInfo = action.payload.processId ? ` (Process: ${action.payload.processId})` : '';
      const notification: NotificationData = {
        id: `upload_success_${Date.now()}`,
        type: 'success',
        title: 'Uploaded Successfully',
        message: `${action.payload.filename || 'Files'} uploaded successfully${processInfo}.`,
        duration: 10000,
        userId: action.payload.userId,
        createdAt: Date.now()
      };
      state.notifications.push(notification);
    },
    
    addUploadFailedNotification: (state, action: PayloadAction<{ userId: string; error?: string; processId?: string }>) => {
      const processInfo = action.payload.processId ? ` (Process: ${action.payload.processId})` : '';
      const notification: NotificationData = {
        id: `upload_failed_${Date.now()}`,
        type: 'error',
        title: 'Upload Failed',
        message: `${action.payload.error || 'Failed to upload files. Please try again.'}${processInfo}`,
        duration: 10000,
        userId: action.payload.userId,
        createdAt: Date.now()
      };
      state.notifications.push(notification);
    },
    
    addProcessingStartedNotification: (state, action: PayloadAction<{ userId: string; summaryId: string; processId?: string }>) => {
      const processInfo = action.payload.processId || action.payload.summaryId;
      const notification: NotificationData = {
        id: `processing_started_${Date.now()}`,
        type: 'info',
        title: 'Summary Generation Started',
        message: `Summary generation process has started successfully (Process: ${processInfo}). This may take some time, you will receive a notification when it's ready.`,
        duration: 10000,
        userId: action.payload.userId,
        createdAt: Date.now()
      };
      state.notifications.push(notification);
    },
    
    addSummaryReadyNotification: (state, action: PayloadAction<{ userId: string; summaryId: string; title?: string; processId?: string }>) => {
      const processInfo = action.payload.processId || action.payload.summaryId;
      const notification: NotificationData = {
        id: `summary_ready_${Date.now()}`,
        type: 'success',
        title: 'Summary Generated Successfully',
        message: `${action.payload.title || 'Summary'} is ready and available for viewing (Process: ${processInfo}).`,
        duration: 0, // Don't auto-close
        actionText: 'View Summary',
        actionUrl: '/anis/summaries',
        userId: action.payload.userId,
        createdAt: Date.now()
      };
      state.notifications.push(notification);
    },
    
    addDateRangeSuccessNotification: (state, action: PayloadAction<{ userId: string; dateRange?: string; processId?: string }>) => {
      const processInfo = action.payload.processId ? ` (Process: ${action.payload.processId})` : '';
      const notification: NotificationData = {
        id: `daterange_success_${Date.now()}`,
        type: 'success',
        title: 'Date Range Export Successful',
        message: `Date range export for ${action.payload.dateRange || 'selected dates'} has been completed successfully${processInfo}.`,
        duration: 10000,
        userId: action.payload.userId,
        createdAt: Date.now()
      };
      state.notifications.push(notification);
    },
    
    addDateRangeFailedNotification: (state, action: PayloadAction<{ userId: string; error?: string; processId?: string }>) => {
      const processInfo = action.payload.processId ? ` (Process: ${action.payload.processId})` : '';
      const notification: NotificationData = {
        id: `daterange_failed_${Date.now()}`,
        type: 'error',
        title: 'Date Range Export Failed',
        message: `${action.payload.error || 'Failed to export date range. Please try again.'}${processInfo}`,
        duration: 10000,
        userId: action.payload.userId,
        createdAt: Date.now()
      };
      state.notifications.push(notification);
    }
  }
});

export const {
  addNotification,
  removeNotification,
  clearNotifications,
  clearUserNotifications,
  addUploadSuccessNotification,
  addUploadFailedNotification,
  addProcessingStartedNotification,
  addSummaryReadyNotification,
  addDateRangeSuccessNotification,
  addDateRangeFailedNotification
} = notificationSlice.actions;

export default notificationSlice.reducer;